import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, CircularProgress, Button, Stack } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterPanel from '../Filters/FilterPanel';
import SignalsTable from '../Table/SignalsTable';
import { getSignals, getOltIps } from '../../services/api';

const formatPortNumber = (portNumber) => {
  if (!portNumber || isNaN(portNumber)) return 'N/A';

  if (portNumber < 16779009) {
    return String(portNumber);
  }

  const port = Math.floor((portNumber - 16779009) / 256) + 1;
  const subPort = ((portNumber - 16779009) % 256) + 1;
  return `${port}/${subPort}`;
};

const formatSignalValue = (value) => {
  if (!value) return -40;
  return value / 100;
};

const formatDate = (dateString) => {
  if (!dateString) return 'Нет данных';

  const date = new Date(dateString);
  date.setHours(date.getHours() - 7);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const escapeCsvCell = (value) => {
  const stringValue = value == null ? '' : String(value);
  const escaped = stringValue.replace(/"/g, '""');
  return `"${escaped}"`;
};

const escapeHtml = (value) => {
  const stringValue = value == null ? '' : String(value);
  return stringValue
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const Dashboard = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  // Реальные счётчики портов по каждому OLT (по всем данным, не только текущая страница).
  const [oltStats, setOltStats] = useState({});
  const [filters, setFilters] = useState({
    olt_ip: '',
    description: '',
    search: '',
    signal_condition: '',
    page: 0
  });
  const [oltIps, setOltIps] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    total: 0
  });
  const [isCustomDataSource, setIsCustomDataSource] = useState(false);

  useEffect(() => {
    fetchOltIps();
  }, []);

  useEffect(() => {
    fetchSignals();
  }, [filters]);

  const fetchOltIps = async () => {
    try {
      const data = await getOltIps();
      setOltIps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching OLT IPs:', err);
      setError('Ошибка при загрузке списка OLT');
    }
  };

  const fetchSignals = async () => {
    setLoading(true);
    try {
      const response = await getSignals(filters);
      console.log('API response:', response);
      
      if (response && response.data) {
        setSignals(response.data);
        setOltStats(response.oltStats || {});
        setPagination({
          page: filters.page,
          total: response.total || 0
        });
      } else {
        console.error('Invalid response format:', response);
        setSignals([]);
        setOltStats({});
      }
    } catch (error) {
      console.error('Error fetching signals:', error);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    if (newFilters.type === 'problemPorts') {
      setIsCustomDataSource(true);
      // У кастомного источника нет серверной статистики по OLT — сбрасываем,
      // чтобы в заголовках групп не остались счётчики от предыдущей выборки.
      setOltStats({});
      console.log('Problem ports data:', newFilters.data);
      
      if (newFilters.data && Array.isArray(newFilters.data.data)) {
        const transformedData = newFilters.data.data.map(port => {
          return {
            olt_ip: port.olt_ip,
            // Сервер уже отдаёт eltex-порт в port_number (раньше тут был port_id
            // в «китайском» формате — описание/номер не находились).
            port_number: port.port_number != null ? parseInt(port.port_number) : null,
            port_description: port.port_description || 'Нет описания',
            olt_description: port.olt_description || '',
            signal_olt: -4000,
            signal_onu: -4000,
            date: port.last_problem_time || new Date().toISOString()
          };
        });
        
        setSignals(transformedData);
        setPagination({
          page: 0,
          total: newFilters.data.total || transformedData.length
        });

        // Раньше тут был второй раунд enrichment (getPortDescriptions /
        // getCurrentSignalsBulk) по сыровому onu_status.port_id — он не находил
        // совпадений в pon_signals из-за разных форматов кодирования порта и
        // затирал корректные данные пустыми. Теперь сервер отдаёт уже готовые
        // eltex port_number + port_description, доп. запросы не нужны.
      } else {
        console.error('Invalid problem ports data format:', newFilters.data);
        setSignals([]);
        setPagination({
          page: 0,
          total: 0
        });
      }
    } else if (newFilters.type === 'dropouts') {
      // Источник «недавно упали»: результат детектора переходов UP→DOWN (LOS).
      // Разворачиваем affected[] веток в плоский список строк таблицы, сохраняя
      // dropped_count/severity для заголовков групп (через oltStats).
      setIsCustomDataSource(true);
      setOltStats({});

      const generatedAt = newFilters.data?.generated_at;

      if (newFilters.data && Array.isArray(newFilters.data.branches)) {
        // Сортируем ветки по числу упавших (массовые сверху) — задаёт порядок групп.
        const orderedBranches = [...newFilters.data.branches].sort(
          (a, b) => b.dropped_count - a.dropped_count
        );

        const transformedData = [];
        const dropStats = {}; // olt_ip -> { dropped, severity }
        orderedBranches.forEach((branch) => {
          if (branch.affected && branch.affected.length) {
            const dropped = branch.dropped_count;
            const existing = dropStats[branch.olt_ip] || { dropped: 0, severity: false };
            dropStats[branch.olt_ip] = {
              dropped: existing.dropped + dropped,
              severity: existing.severity || branch.severity,
            };
            branch.affected.forEach((a) => {
              transformedData.push({
                olt_ip: branch.olt_ip,
                port_number: Number.isFinite(Number(a.port_number))
                  ? Number(a.port_number)
                  : (a.port_id != null ? parseInt(a.port_id, 10) : null),
                olt_description: branch.olt_description || '',
                port_description: `ветка 0/0/${branch.pon_port}`,
                signal_olt: -4000,
                signal_onu: -4000,
                date: a.timestamp || generatedAt || new Date().toISOString(),
                _branch_severity: branch.severity,
                _branch_dropped: dropped,
              });
            });
          }
        });

        setSignals(transformedData);
        setOltStats(dropStats);
        setPagination({ page: 0, total: transformedData.length });
      } else {
        setSignals([]);
        setPagination({ page: 0, total: 0 });
      }
    } else {
      setIsCustomDataSource(false);
      setFilters({ ...newFilters, page: 0 });
    }
  };

  const handlePageChange = (newPage) => {
    console.log('Page changed to:', newPage);
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleRowClick = (row) => {
    console.log('Row clicked:', row);
  };

  const downloadBlob = (content, mimeType, filename) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const mapExportRows = (rows) => {
    const oltDescriptionMap = new Map(
      (oltIps || []).map((item) => [item.ip, item.description || item.ip])
    );

    return rows.map((row) => ({
      olt: row.olt_description || oltDescriptionMap.get(row.olt_ip) || row.olt_ip,
      port: formatPortNumber(row.port_number),
      description: row.port_description || 'Нет описания',
      signalOlt: `${formatSignalValue(row.signal_olt).toFixed(2)} dBm`,
      signalOnu: `${formatSignalValue(row.signal_onu).toFixed(2)} dBm`,
      date: formatDate(row.date)
    }));
  };

  const fetchAllRowsForExport = async () => {
    // Для нестандартных наборов (например, problem ports) экспортируем то, что уже на экране.
    if (isCustomDataSource) {
      return signals;
    }

    const pageSize = 1000;
    const firstPageResponse = await getSignals({ ...filters, page: 0, limit: pageSize });
    const total = firstPageResponse?.total || 0;
    const allRows = [...(firstPageResponse?.data || [])];

    if (total <= allRows.length) {
      return allRows;
    }

    const totalPages = Math.ceil(total / pageSize);
    for (let page = 1; page < totalPages; page += 1) {
      const pageResponse = await getSignals({ ...filters, page, limit: pageSize });
      if (Array.isArray(pageResponse?.data) && pageResponse.data.length > 0) {
        allRows.push(...pageResponse.data);
      }
    }

    return allRows;
  };

  const exportAsCsv = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllRowsForExport();
      const exportRows = mapExportRows(allRows);
      const headers = ['OLT', 'Порт', 'Описание', 'Сигнал OLT (dBm)', 'Сигнал ONU (dBm)', 'Дата'];
      const csvRows = exportRows.map((row) => [
        row.olt,
        row.port,
        row.description,
        row.signalOlt,
        row.signalOnu,
        row.date
      ]);

      const csvContent = [
        headers.map(escapeCsvCell).join(','),
        ...csvRows.map((row) => row.map(escapeCsvCell).join(','))
      ].join('\n');

      downloadBlob(`\uFEFF${csvContent}`, 'text/csv;charset=utf-8;', 'signals_export.csv');
    } catch (exportError) {
      console.error('Error exporting CSV:', exportError);
    } finally {
      setExportLoading(false);
    }
  };

  const exportAsXls = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllRowsForExport();
      const exportRows = mapExportRows(allRows);

      const tableRows = exportRows
        .map(
          (row) =>
            `<tr><td>${escapeHtml(row.olt)}</td><td>${escapeHtml(row.port)}</td><td>${escapeHtml(row.description)}</td><td>${escapeHtml(row.signalOlt)}</td><td>${escapeHtml(row.signalOnu)}</td><td>${escapeHtml(row.date)}</td></tr>`
        )
        .join('');

      const xlsContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
          </head>
          <body>
            <table>
              <tr>
                <th>OLT</th>
                <th>Порт</th>
                <th>Описание</th>
                <th>Сигнал OLT (dBm)</th>
                <th>Сигнал ONU (dBm)</th>
                <th>Дата</th>
              </tr>
              ${tableRows}
            </table>
          </body>
        </html>
      `.trim();

      downloadBlob(xlsContent, 'application/vnd.ms-excel;charset=utf-8;', 'signals_export.xls');
    } catch (exportError) {
      console.error('Error exporting XLS:', exportError);
    } finally {
      setExportLoading(false);
    }
  };

  if (error) {
    return (
      <Container sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="h6">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        width: '100%',
        maxWidth: '100% !important',
        padding: '24px'
      }}
    >
      <FilterPanel 
        filters={filters} 
        onFilterChange={handleFilterChange}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadIcon />}
          disabled={exportLoading || loading}
          onClick={exportAsCsv}
        >
          Экспорт CSV
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FileDownloadIcon />}
          disabled={exportLoading || loading}
          onClick={exportAsXls}
        >
          Экспорт XLS
        </Button>
      </Stack>
      <Box sx={{ height: '80vh', width: '100%', mt: 2 }}>
        {loading && signals.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <SignalsTable
            data={signals}
            loading={loading}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            totalRows={pagination.total}
            oltStats={oltStats}
          />
        )}
      </Box>
    </Container>
  );
};

export default Dashboard; 